<template>
    <div class="profile-page">
        <LoadingBar v-if="loading" message="Loading User Profile..." />

        <div v-else class="container-xl">

            <!-- Breadcrumb -->
            <nav class="profile-breadcrumb" aria-label="breadcrumb">
                <a href="#" @click.prevent="$router.back()">
                    <i class="fa fa-arrow-left"></i> Users
                </a>
                <span class="sep">/</span>
                <span class="current">{{ user.first_name }} {{ user.last_name }}</span>
            </nav>

            <!-- Header -->
            <div class="profile-header">
                <div class="profile-header-icon" style="background:var(--pp-info-light);color:var(--pp-info);">
                    <img v-if="user.photo" :src="getFileUrl(user.photo)"
                        style="width:100%;height:100%;object-fit:cover;border-radius:12px;" alt="User" />
                    <span v-else style="font-size:28px;font-weight:600;">{{ initials }}</span>
                </div>
                <div class="profile-header-body">
                    <h3>{{ user.first_name }} {{ user.last_name }}</h3>
                    <div class="profile-header-meta">
                        <span><i class="fa fa-hashtag"></i>{{ user.external_id || 'N/A' }}</span>
                        <span v-if="user.email"><i class="fa fa-envelope"></i>{{ user.email }}</span>
                        <span v-if="user.get_current_company_role?.name"><i class="fa fa-user-shield"></i>{{ user.get_current_company_role?.name}}</span>
                    </div>
                </div>
                <div class="profile-actions">
                    <span class="profile-badge" :class="user.status === 'active' ? 'c-success' : 'c-danger'">
                        <span class="dot"></span> {{ user.status === 'active' ? 'Active' : 'Inactive' }}
                    </span>
                    <button type="button" class="action-btn action-btn-back" @click="$router.back()">
                        <i class="fa fa-arrow-left"></i> Back
                    </button>
                    <button type="button" class="action-btn action-btn-primary" @click.prevent="edit(user)">
                        <i class="fa fa-pencil"></i> Edit
                    </button>
                </div>
            </div>

            <!-- Stat bar -->
            <div class="profile-stats">
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-info">
                        <i class="fa fa-user"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Full Name</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ user.first_name }} {{ user.last_name }}
                        </div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-accent">
                        <i class="fa fa-user-shield"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Role</div>
                        <div class="profile-stat-value">{{ user.get_current_company_role?.name || 'User' }}</div>
                    </div>
                </div>
                <!-- <div class="profile-stat-card">
                    <div class="profile-stat-icon c-warning">
                        <i class="fa fa-venus-mars"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Gender</div>
                        <div class="profile-stat-value">{{ user.gender || 'N/A' }}</div>
                    </div>
                </div> -->
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-success">
                        <i class="fa fa-calendar"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Joined</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ formatDate(user.created_at) || 'N/A' }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main layout -->
            <div class="row g-3">

                <!-- Left: User details -->
                <div class="col-lg-12 d-flex flex-column gap-3">

                    <!-- Core details -->
                    <div class="profile-card">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-info-circle"></i> User Details
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-field-grid">
                                <div class="profile-field" v-if="user.first_name || user.last_name">
                                    <span class="profile-label">Full Name</span>
                                    <span class="profile-value">{{ user.first_name }} {{ user.last_name }}</span>
                                </div>
                                <div class="profile-field" v-if="user.email">
                                    <span class="profile-label">Email</span>
                                    <span class="profile-value">
                                        <a :href="`mailto:${user.email}`" 
                                            style="color:var(--pp-accent);text-decoration:none;">
                                            {{ user.email }}
                                        </a>
                                    </span>
                                </div>
                                <div class="profile-field" v-if="user.phone">
                                    <span class="profile-label">Phone</span>
                                    <span class="profile-value">
                                        <a :href="`tel:${user.phone}`" 
                                            style="color:var(--pp-accent);text-decoration:none;">
                                            {{ user.phone }}
                                        </a>
                                    </span>
                                </div>
                                <div class="profile-field" v-if="user.dob">
                                    <span class="profile-label">Date of Birth</span>
                                    <span class="profile-value">{{ formatDate(user.dob) }}</span>
                                </div>
                                <div class="profile-field" v-if="user.gender">
                                    <span class="profile-label">Gender</span>
                                    <span class="profile-value">{{ user.gender }}</span>
                                </div>
                                <div class="profile-field" v-if="user.marital_status">
                                    <span class="profile-label">Marital Status</span>
                                    <span class="profile-value">{{ user.marital_status }}</span>
                                </div>
                                <div class="profile-field" v-if="user.insurance">
                                    <span class="profile-label">Insurance</span>
                                    <span class="profile-value">{{ user.insurance }}</span>
                                </div>
                                <div class="profile-field" v-if="user.status">
                                    <span class="profile-label">Status</span>
                                    <span class="profile-value">
                                        <span class="profile-badge" :class="user.status === 'active' ? 'c-success' : 'c-danger'">
                                            <span class="dot"></span>
                                            {{ user.status === 'active' ? 'Active' : 'Inactive' }}
                                        </span>
                                    </span>
                                </div>
                                <div class="profile-field" v-if="user.created_at">
                                    <span class="profile-label">Created At</span>
                                    <span class="profile-value">{{ formatDate(user.created_at) }}</span>
                                </div>
                            </div>

                            <!-- Address: full-width field -->
                            <div class="profile-field mt-3" v-if="user.address"
                                style="grid-column:1/-1;">
                                <span class="profile-label">Address</span>
                                <span class="profile-value" style="line-height:1.6;">
                                    {{ user.address }}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Right: Role & Permissions sidebar -->
                <!-- <div class="col-lg-4 d-flex flex-column gap-3">
                    <div class="profile-card">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-accent);">
                                <i class="fa fa-shield-alt" style="color:var(--pp-accent);"></i> Role & Permissions
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-field" style="flex-direction:row;padding:10px 12px;">
                                <i class="fa fa-user-shield" style="width:20px;color:var(--pp-text-muted);"></i>
                                <span class="profile-value">
                                    <span class="profile-badge c-accent" style="font-size:13px;">
                                        {{ user.get_current_company_role?.name || 'User' }}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>

                </div> -->

            </div>

            <!-- Full-width Activity card (kept unchanged as requested) -->
            <div class="profile-card mt-3">
                <div class="profile-card-header">
                    <div class="profile-section-title" style="color:var(--pp-amber-text);">
                        <i class="fa fa-history" style="color:var(--pp-amber-text);"></i> User Activity
                    </div>
                    <span v-if="user.get_logs && user.get_logs.length > 0" class="profile-count-pill">
                        {{ user.get_logs.length }}
                    </span>
                </div>
                <div class="profile-card-body">
                    <!-- Logs exist -->
                    <div v-if="user.get_logs && user.get_logs.length > 0" class="activity-timeline">
                        <div v-for="log in user.get_logs" :key="log.id" class="activity-item">
                            <!-- Icon column -->
                            <div class="activity-icon-wrap">
                                <div class="activity-icon">
                                    <i class="fa fa-bolt"></i>
                                </div>
                                <div class="activity-line"></div>
                            </div>

                            <!-- Content column -->
                            <div class="activity-body">
                                <div class="activity-top">
                                    <span class="activity-action">{{ log.activity }}</span>
                                    <span class="activity-badge-profile">
                                        {{ log.loggable_type.split('\\').pop().replace(/_/g, ' ') }} #{{ log.loggable_id }}
                                    </span>
                                </div>

                                <!-- Meta chips -->
                                <div class="activity-meta-row" v-if="log.meta">
                                    <span v-for="(val, key) in JSON.parse(log.meta)" :key="key" class="activity-meta-chip">
                                        <i class="fa fa-tag"></i>
                                        {{ key.replace(/_/g, ' ') }}: <strong>{{ val }}</strong>
                                    </span>
                                </div>

                                <div class="activity-footer">
                                    <span class="activity-time">
                                        <i class="fa fa-clock"></i>
                                        {{ new Date(log.created_at).toLocaleString('en-GB', {
                                            day: '2-digit', month: 'short', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit' }) }}
                                    </span>
                                    <span class="activity-log-id">
                                        <i class="fa fa-hashtag"></i> Log #{{ log.id }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Empty state -->
                    <div v-else class="profile-empty">
                        <i class="fa fa-stream"></i>
                        <p>No activity recorded for this user yet.</p>
                    </div>
                </div>
            </div>

        </div>
    </div>

    <FormModal ref="editModal" title="Edit User" id="edit_user_form_modal">
        <EditUser :roles="roles" :user="selectedUser" ref="editUserForm" @created="onUpdated"
          @close="closeEditModal('editModal')" />
      </FormModal>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import EditUser from '@/views/User/EditUser.vue'

export default {
  name: 'UserProfile',
  components: { LoadingBar ,FormModal, EditUser, },

  data() {
    return {
      user: {},
         logs: [],
      loading: false,
      external_id: this.$route.params.external_id
    }
  },

  computed: {
    initials() {
      const f = this.user.first_name?.[0] || ''
      const l = this.user.last_name?.[0] || ''
      return (f + l).toUpperCase()
    }
  },

  mounted() {
    this.fetchProfile()
    window.addEventListener('resize', this.syncHeight)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.syncHeight)
  },

  methods: {
    formatDate(val) {
      if (!val) return ''
      return new Date(val).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    },

    syncHeight() {
      const info = this.$refs.infoCard
      const perm = this.$refs.permCard
      const scroll = this.$refs.permScroll
      const hdr = this.$refs.permHdr
      const roleWrap = this.$refs.roleWrap
      if (!info || !perm || !scroll) return

      const h = info.offsetHeight
      perm.style.height = h + 'px'

      const used = (perm.querySelector('.top-bar')?.offsetHeight || 0)
        + (hdr?.offsetHeight || 0)
        + (roleWrap?.offsetHeight || 0)

      scroll.style.maxHeight = (h - used) + 'px'
      scroll.style.overflowY = 'auto'
    },

    async fetchProfile() {
      this.loading = true
      const result = await form_api_submit({
        url: `user/profile/${this.external_id}`,
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })
      if (result.success) {
        this.user = result.data
        this.$nextTick(this.syncHeight)
      }
      this.loading = false
    },

      edit(user) {
      this.selectedUser = user
      this.$refs.editModal.showModal()
    },
    closeEditModal(ref) {
      this.$refs[ref].closeModal()
    },
   async onUpdated() {
    setTimeout(async () => {
      await this.fetchProfile()
    }, 500)
  },
  }
}
</script>